import { AxiosError } from "axios";
import "./index.css";

import * as serviceWorker from "./serviceWorker";

import App from "./App";
import { Provider, connect } from "react-redux";
import React, { Suspense } from "react";
import ReactDOM from "react-dom/client";
import { iRootState, store } from "./rematch/store";
import SpinnerWrapper from "./gantt/components/common/SpinnerWrapper";
import { LoginScreen } from "./gantt/components/login/LoginScreen";
import { createMfilesHttpClient, loginMfilesHttpClientAsync, getStoredApiToken, getAuthenticationProtocols, loginMfilesHttpClientOAuthAsync, setPresharedKey, clearPresharedKey, validateApiToken, clearStoredApiToken } from "./backend/configuration/authentication";
import { getConfigFromNVSAsync } from "./backend/services/NamedValueStorageService";
import { getConfig, parsePresharedKeyFromQueryString, parseTokenFromQueryString, removeQueryParameter, parseRefreshFromQueryString, resolveMFilesApiBaseUrl, resolveVaultGuid } from "./backend/helpers/urlHelper";

import { IAuthenticationInformation } from "./backend/interfaces/configuration/IAuthenticationInformation";

import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { MsalProvider } from "@azure/msal-react";
import { AccountInfo, AuthenticationResult, Configuration, InteractionRequiredAuthError, IPublicClientApplication, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import ganttDB from "./backend/storage/database";
import { QUERYSTRING_PRESHARED_KEY, QUERYSTRING_TOKEN_PART } from "./backend/constants/common";
import { IExternalConfiguration } from "./backend/interfaces/configuration/IExternalConfiguration";
import { IBaseConfiguration } from "./backend/interfaces/configuration/IBaseConfiguration";
import { ErrorBoundary } from "react-error-boundary";
import { ErrorForm } from "./gantt/components/common/ErrorForm";
import { logException, logWarning, LOGEXCEPTION_PROMISE_REJECTED, LOGWARNING_ADLOGIN_FAILED, LOGWARNING_MFLOGIN_FAILED } from "./backend/services/LoggingService";

window.addEventListener('unhandledrejection', (event) => logException(LOGEXCEPTION_PROMISE_REJECTED, event.reason));

let msalInstance: IPublicClientApplication = null;

let azureLoginScopes = [];
let useIdTokenAsAccessToken = true;

const render = () => {
  const container = document.getElementById("root");
  const root = ReactDOM.createRoot(container);
  root.render(
    <ErrorBoundary
      FallbackComponent={ErrorForm}
      onReset={() => {
        // reset the state of your app so the error doesn't happen again
      }}
    >
      <React.StrictMode>
        <Provider store={store}>
          <I18nextProvider i18n={i18n}>
            <Suspense fallback={<SpinnerWrapper />}>
              <MsalProvider instance={msalInstance}>
                <AppContainer />
              </MsalProvider>
            </Suspense>
          </I18nextProvider>
        </Provider>
      </React.StrictMode>
    </ErrorBoundary>
  );
};

interface IAppContainerBaseProps {
  isAuthenticated: boolean;
  loadingConfiguration: boolean;
  loginError: string;
  isOauth2Enabled: boolean;
}

// Render login screen or gantt
const AppContainer = connect((rootState: iRootState) => ({
  isAuthenticated: rootState.common.isAuthenticated,
  loadingConfiguration: rootState.common.loadingConfiguration,
  loginError: rootState.common.loginError,
  isOauth2Enabled: rootState.common.isOauth2Enabled,
}))((props: IAppContainerBaseProps) => {
  const loginScreenProps = {
    tryLogin: (username, password, isWindowsUser, rememberMe) => tryLogin(username, password, isWindowsUser, rememberMe),
    tryAzureLogin: (account) => tryAzureLogin(account),
    setErrorMessage: (message) => store.dispatch.common.loginFailed(message),
    errorMessage: props.loginError,
    isOauth2Enabled: props.isOauth2Enabled,
  }
  return (
    <>
      {!props.isAuthenticated && !props.loadingConfiguration && <LoginScreen {...loginScreenProps} />}
      {props.loadingConfiguration && <SpinnerWrapper />}
      {props.isAuthenticated && !props.loadingConfiguration && <App />}
    </>
  );
});

const tryLogin = async (username: string, password: string, isWindowsUser: boolean, rememberMe: boolean): Promise<string | null> => {
  if(parseTokenFromQueryString()) {
    window.history.replaceState({}, "", removeQueryParameter(window.location.href,QUERYSTRING_TOKEN_PART));
  }
  try {
    ganttDB.clearMetadata();
    const baseConfiguration = await getConfig();
    const externalConfig = await getConfigFromNVSAsync(baseConfiguration);

    if (baseConfiguration.devModeOn) {
      store.dispatch.common.initializeModelsAsync({
        httpClient: null,
        externalConfig: externalConfig,
        baseConfiguration: baseConfiguration,
      });
    } else {
      const mfilesApiBaseUrl = resolveMFilesApiBaseUrl(baseConfiguration, externalConfig);
      const vaultGuid = resolveVaultGuid(baseConfiguration, externalConfig);
      const authenticationInformation: IAuthenticationInformation = {
        Username: username,
        Password: password,
        VaultGuid: vaultGuid,
        WindowsUser: isWindowsUser,
      };
      const httpClient = await loginMfilesHttpClientAsync(mfilesApiBaseUrl, authenticationInformation, rememberMe);
      store.dispatch.common.initializeModelsAsync({ httpClient, externalConfig, baseConfiguration: baseConfiguration });
    }
  } catch (err) {
    const axiosError: AxiosError = err;
    const axiosData: any = axiosError?.response?.data;
    logWarning(LOGWARNING_MFLOGIN_FAILED, axiosData?.Message ?? err?.message ?? i18n.t("error.loginunauthorized"), username);
    if (axiosError.response?.status === 403) {
      return i18n.t("error.loginunauthorized");
    }
    return i18n.t("error.loginfailed");
  }

  // Everything OK -> return no error message
  return null;
};

const tryAzureLogin = async (account: AccountInfo): Promise<void> => {
  try {
    if(account === null) {
      ganttDB.clearMetadata();
      await msalInstance.loginRedirect({scopes: azureLoginScopes});
    } else {
      const request = {account: account, scopes: azureLoginScopes};
      let authResult: AuthenticationResult;
      try {
        authResult = await msalInstance.acquireTokenSilent(request);
        await handleAzureLogin(authResult);
      } catch(e) {
        if(e instanceof InteractionRequiredAuthError) {
          await msalInstance.acquireTokenRedirect(request);
        } else {
          handleAzureLoginError(e, authResult?.account?.username);
        }
      }
    }
  } catch(error) {
    handleAzureLoginError(error, account?.username);
  }
}

const azureTokenRefresh = async () => {
  const account = msalInstance.getActiveAccount();
  const request = {account: account, scopes: azureLoginScopes};
  const httpClient = store.getState().common.httpClient;
  const headers = httpClient.defaults.headers;
  const currentAuthorization = headers["Authorization"];
  try {
    const authResult = await msalInstance.acquireTokenSilent(request);
    const token = useIdTokenAsAccessToken ? authResult.idToken : authResult.accessToken;
    const authorization = `Bearer ${token}`;
    if(authorization !== currentAuthorization) {
      headers["Authorization"] = authorization;
    }
  } catch(error) {
    if(error instanceof InteractionRequiredAuthError) {
      await msalInstance.acquireTokenRedirect(request);
    }
  }
}

const handleAzureLoginError = (error: Error, username?: string) => {
  let message = i18n.t("error.loginfailed");
  const axiosError = error as AxiosError;
  const axiosData: any = axiosError?.response?.data;
  logWarning(LOGWARNING_ADLOGIN_FAILED, axiosData?.Message ?? error?.message ?? i18n.t("error.loginunauthorized"), username);
  if (axiosError?.response?.status === 403) {
    message = i18n.t("error.loginunauthorized");
  }
  store.dispatch.common.loginFailed(message);
}

const handleAzureLogin = async (authResult: AuthenticationResult) => {
  store.dispatch.common.showLoadingSpinner(true);
  const baseConfiguration = await getConfig();
  const externalConfig = await getConfigFromNVSAsync(baseConfiguration);
  const mfilesApiBaseUrl = resolveMFilesApiBaseUrl(baseConfiguration, externalConfig);
  const vaultGuid = resolveVaultGuid(baseConfiguration, externalConfig);
  const authProtocols = await getAuthenticationProtocols(mfilesApiBaseUrl, vaultGuid);
  const authProtocol = authProtocols.find(a => a.IsDefault === true);

  useIdTokenAsAccessToken = authProtocol.Configuration.UseIdTokenAsAccessToken === "true" ? true : false;
  msalInstance.setActiveAccount(authResult.account);
  const token = useIdTokenAsAccessToken ? authResult.idToken : authResult.accessToken;

  const httpClient = await loginMfilesHttpClientOAuthAsync(mfilesApiBaseUrl, token, vaultGuid);
  store.dispatch.common.initializeModelsAsync({ httpClient, externalConfig, baseConfiguration: baseConfiguration });

  setInterval(azureTokenRefresh, 60000);
}

const initializePresharedKey = () => {
  const presharedKey = parsePresharedKeyFromQueryString();
  if(presharedKey) {
    if(presharedKey === "null") {
      clearPresharedKey();
    } else {
      setPresharedKey(presharedKey);
    }
    window.history.replaceState({}, "", removeQueryParameter(window.location.href,QUERYSTRING_PRESHARED_KEY));
  }
}

const initializeRefresh = () => {
  const refresh = parseInt(parseRefreshFromQueryString());
  if(refresh >= 5) {
    setTimeout(function () {
      window.location.reload();
    }, refresh * 1000);
  }
}

const initializeMsalInstance = async (mfilesApiBaseUrl: string, vaultGuid: string) => {
  const authProtocols = await getAuthenticationProtocols(mfilesApiBaseUrl, vaultGuid);
  const authProtocol = authProtocols.find(a => a.IsDefault === true);

  azureLoginScopes = authProtocol?.Configuration?.Scope?.split(" ");

  const msalConfig: Configuration = {
    auth: {
      clientId: authProtocol?.Configuration?.ClientID,
      authority: authProtocol?.Configuration?.AuthorizationEndpoint.split("/oauth2")[0],
      redirectUri: window.location.origin + process.env.PUBLIC_URL,
      postLogoutRedirectUri: window.location.origin + process.env.PUBLIC_URL,
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        loggerCallback: (level, message, containsPii) => {
          if(containsPii) {
            return;
          }
          switch(level) {
            case LogLevel.Error:
              console.error(message);
              break;
            case LogLevel.Warning:
              console.warn(message);
              break;
          }
        },
        piiLoggingEnabled: false
      }
    }
  };

  store.dispatch.common.useOauth2(msalConfig.auth.clientId ? true : false);

  return new PublicClientApplication(msalConfig);
}

const getTokenValidation = async (mfilesApiBaseUrl: string) => {
  let apiKey = parseTokenFromQueryString();
  let apiKeyValid = false;
  let authMessage: string;
  let tokenAuth = false;
  if (apiKey !== null) {
    clearStoredApiToken();
    [apiKeyValid, authMessage] = await validateApiToken(mfilesApiBaseUrl, apiKey);
    tokenAuth = true;
  } else {
    apiKey = getStoredApiToken();
    if(apiKey) {
      [apiKeyValid, authMessage] = await validateApiToken(mfilesApiBaseUrl, apiKey);
    }
  }
  return {
    apiKey,
    apiKeyValid,
    authMessage,
    tokenAuth
  };
}

const passwordLoginFlow = async (externalConfig: IExternalConfiguration, baseConfiguration: IBaseConfiguration) => {
  const mfilesApiBaseUrl = resolveMFilesApiBaseUrl(baseConfiguration, externalConfig);
  const auth = await getTokenValidation(mfilesApiBaseUrl);
  if(auth.apiKeyValid === true) {
    const httpClient = createMfilesHttpClient(mfilesApiBaseUrl, auth.apiKey);
    store.dispatch.common.initializeModelsAsync({
      httpClient: httpClient,
      externalConfig: externalConfig,
      baseConfiguration: baseConfiguration,
    });
  } else {
    if(auth.apiKey) {
      if(auth.tokenAuth) {
        if(auth.authMessage) {
          store.dispatch.common.loginFailed(auth.authMessage);
        }
      } else {
        //Invalid api token, force logout
        store.dispatch.common.logout();
      }
    }
  }
}

const initialize = async () => {
  initializePresharedKey();
  initializeRefresh();
  const baseConfiguration = await getConfig();
  const externalConfig = await getConfigFromNVSAsync(baseConfiguration);
  const mfilesApiBaseUrl = resolveMFilesApiBaseUrl(baseConfiguration, externalConfig);
  const vaultGuid = resolveVaultGuid(baseConfiguration, externalConfig);
  msalInstance = null;
  if (baseConfiguration.devModeOn) {
    store.dispatch.common.initializeModelsAsync({ httpClient: null, externalConfig, baseConfiguration });
  } else {
    msalInstance = await initializeMsalInstance(mfilesApiBaseUrl, vaultGuid);

    //Redirect
    let authResult: AuthenticationResult = null;
    try {
      authResult = await msalInstance.handleRedirectPromise();
      if(authResult) {
        await handleAzureLogin(authResult);
      }
    } catch(error) {
      handleAzureLoginError(error, authResult?.account?.username);
    }

    let account = msalInstance.getActiveAccount();
    const accounts = msalInstance.getAllAccounts();
    if(!account && accounts && accounts.length === 1) {
      account = accounts[0];
    }
    if(account) {
      //Azure account has been logged in
      const request = {account: account, scopes: azureLoginScopes};
      let authResult2: AuthenticationResult = null;
      try {
        authResult2 = await msalInstance.acquireTokenSilent(request);
        await handleAzureLogin(authResult2);
      } catch(e) {
        handleAzureLoginError(e, authResult2?.account?.username);
      }
    } else {
      await passwordLoginFlow(externalConfig, baseConfiguration);
    }
  }
  render();
};

// eslint-disable-next-line no-restricted-globals
history.scrollRestoration = "manual";

initialize();

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA

serviceWorker.register({
  onUpdate: registration => {
    const waitingServiceWorker = registration.waiting;
    if (waitingServiceWorker) {
      waitingServiceWorker.addEventListener("statechange", event => {
        //New service worker has been activated, but it has not been enabled yet
        if ((event.target as ServiceWorker)?.state === "activated") {
          setTimeout(async () => {
            try {
              //Reload new configurations to cache
              const baseConfiguration = await getConfig();
              await getConfigFromNVSAsync(baseConfiguration);
            } finally {
              //Reload page to use new version
              window.location.reload();
            }
          });
        }
      });
      //Instruct new service worker to activate instantly without waiting all instances to be closed
      waitingServiceWorker.postMessage({ type: "SKIP_WAITING" });
    }
  }
});
